import React from 'react';
import { Box, Button, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import type { FoodObservation } from './FoodTestVisuals';

interface FoodTestNotebookProps {
  observations: FoodObservation[];
  onChange: (observations: FoodObservation[]) => void;
  onExportPdf: () => void;
  onAutoRecord: () => void;
  onAddManual: () => void;
}

const FoodTestNotebook: React.FC<FoodTestNotebookProps> = ({ observations, onChange, onExportPdf, onAutoRecord, onAddManual }) => {
  const update = (id: string, field: 'observation' | 'inference', value: string) => {
    onChange(observations.map((item) => item.id === id ? { ...item, [field]: value } : item));
  };

  const exportCsv = () => {
    const escape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const header = ['Food sample', 'Test', 'Result', 'Observation', 'Inference', 'Colour', 'Timestamp'];
    const rows = observations.map((item) => [item.foodName, item.test, item.category, item.observation, item.inference, item.colourName, item.timestamp].map(escape).join(','));
    const blob = new Blob([[header.map(escape).join(','), ...rows].join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a'); anchor.href = url; anchor.download = 'food-tests-notebook.csv'; anchor.click(); URL.revokeObjectURL(url);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1.5 }}>
        <Box><Typography variant="h6" fontWeight={800}>Laboratory notebook</Typography><Typography variant="body2" color="text.secondary">Edit observations and record the inference supported by each test.</Typography></Box>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onAutoRecord}>Auto-record current result</Button>
          <Button variant="outlined" size="small" onClick={onAddManual}>Manual row</Button>
        </Stack>
      </Stack>
      <TableContainer>
        <Table size="small" aria-label="Food tests observations notebook">
          <TableHead><TableRow><TableCell>Food</TableCell><TableCell>Test / result</TableCell><TableCell>Observation</TableCell><TableCell>Inference</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
          <TableBody>
            {observations.map((item) => <TableRow key={item.id}>
              <TableCell>{item.foodName}</TableCell>
              <TableCell>{item.test.replaceAll('-', ' ')}<Typography variant="caption" display="block">{item.category}</Typography></TableCell>
              <TableCell><TextField size="small" value={item.observation} inputProps={{ 'aria-label': `Observation for ${item.foodName} ${item.test}` }} onChange={(event) => update(item.id, 'observation', event.target.value)} /></TableCell>
              <TableCell><TextField size="small" value={item.inference} placeholder="What does this indicate?" inputProps={{ 'aria-label': `Inference for ${item.foodName} ${item.test}` }} onChange={(event) => update(item.id, 'inference', event.target.value)} /></TableCell>
              <TableCell><IconButton size="small" aria-label={`Delete ${item.foodName} ${item.test} result`} onClick={() => onChange(observations.filter((record) => record.id !== item.id))}><DeleteOutlineIcon /></IconButton></TableCell>
            </TableRow>)}
            {!observations.length && <TableRow><TableCell colSpan={5} align="center">No observations recorded yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
        <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv}>Export CSV</Button>
        <Button size="small" startIcon={<DownloadIcon />} onClick={onExportPdf}>Export PDF report</Button>
      </Box>
    </Paper>
  );
};

export default FoodTestNotebook;